import React, { useState, useEffect, useRef } from 'react';
import { useDomainConfig } from '@/hooks/useDomainConfig';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useAuth } from '@/contexts/AuthContext';
import { SEO } from './SEO/SEO';
import Header from './Header';
import Footer from './Footer';
import { getBackendUrl } from '@/utils/environment';

interface MediaInfo {
  resolution?: string;
  filesize?: number;
  bitrate?: number;
  streamsize?: number;
  originalUrl?: string;
  platform?: string;
  thumbnail_url?: string;
  actualSizes?: {
    video_stream: number;
    audio_stream: number;
    combined_streams: number;
    final_file: number;
  };
}

interface ProgressState {
  message: string;
  percentage: number;
  details: string;
}

interface MediaErrorState {
  hasError: boolean;
  errorType: 'proxy_expired' | 'network' | 'codec' | 'unknown';
  message: string;
  isRetryable: boolean;
}

const PreviewPage: React.FC = () => {
  const config = useDomainConfig();
  const [title, setTitle] = useState('Loading...');
  const [isAudio, setIsAudio] = useState(false);
  const [mediaInfo, setMediaInfo] = useState<MediaInfo>({});
  const [showMediaInfo, setShowMediaInfo] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState<ProgressState>({
    message: 'Initializing...',
    percentage: 0,
    details: 'Please keep this page open while we process your file.'
  });
  const [downloadUrl, setDownloadUrl] = useState<string>('');
  const [filename, setFilename] = useState('');
  const [showExpiration, setShowExpiration] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('1 hour');
  const [shareStatus, setShareStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [shareMessage, setShareMessage] = useState('');
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [mediaError, setMediaError] = useState<MediaErrorState>({
    hasError: false,
    errorType: 'unknown',
    message: '',
    isRetryable: false
  });

  const { trackEvent } = useGoogleAnalytics();
  const { user } = useAuth();

  const playerRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);
  const hlsRef = useRef<any>(null);
  const mergeTaskStartedRef = useRef(false); // Guard to prevent multiple merge tasks
  const currentTaskIdRef = useRef<string | null>(null); // Track current task ID
  const processedUrlRef = useRef<string | null>(null); // Track processed URL to prevent duplicates
  const shareMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    initializePreview();
    return () => {
      // Cleanup HLS instance if it exists
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      // Reset guards on unmount
      mergeTaskStartedRef.current = false;
      currentTaskIdRef.current = null;
      processedUrlRef.current = null;
    };
  }, []); // Empty dependency array ensures this runs only once

  const getFormattedTitle = (
    prefix: 'Preview' | 'Processing' | 'Download',
    platform: string,
    videoTitle: string,
    forSEOTitle: boolean = false
  ): string => {
    const siteName = config.branding.name;
  
    if (forSEOTitle) {
      if (prefix === 'Download') {
        return `Download "${videoTitle}" - ${platform} | ${siteName}`;
      }
      if (prefix === 'Preview') {
        return `Preview "${videoTitle}" - ${platform} | ${siteName}`;
      }
      return `${prefix}: "${videoTitle}" - ${platform} | ${siteName}`;
    }
  
    return `${prefix} | ${platform} | ${videoTitle}`;
  };

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target as Node)) {
        setShowShareMenu(false);
      }
    };

    if (showShareMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showShareMenu]);

  const initializePreview = () => {
    const params = new URLSearchParams(window.location.search);

    // Check if this is a merge task
    const taskId = params.get('task_id');
    const videoFormatId = params.get('video_format_id');
    const audioFormatId = params.get('audio_format_id');
    const downloadUrl = params.get('url');

    if (taskId && videoFormatId && audioFormatId) {
      // Extract metadata for proper title formatting
      const mediaTitle = params.get('title') ? decodeURIComponent(params.get('title')!) : 'Untitled';
      const url = params.get('url') ? decodeURIComponent(params.get('url')!) : '';

      // Check for source_url parameter first for platform detection (original URL)
      const sourceUrl = params.get('source_url') ? decodeURIComponent(params.get('source_url')!) : '';
      const urlForPlatformDetection = sourceUrl || url;

      const platformName = getPlatformName(urlForPlatformDetection);
      const initialTitle = `Processing | ${platformName} | ${mediaTitle}`;
      setTitle(initialTitle);
      document.title = initialTitle;

      handleMergeTask(`/api/download/start-merge?task_id=${taskId}`, mediaTitle, platformName);
      return;
    }

    // Check if this is a merge operation that needs processing
    if (downloadUrl && decodeURIComponent(downloadUrl).includes('/api/download/start-merge')) {
      const decodedDownloadUrl = decodeURIComponent(downloadUrl);

      // Extract metadata from URL parameters for proper title formatting
      const mediaTitle = params.get('title') ? decodeURIComponent(params.get('title')!) : 'Untitled';
      const mediaFilename = params.get('filename') ? decodeURIComponent(params.get('filename')!) : 'download';
      const audioMode = params.get('is_audio') === 'true';

      // Set initial metadata
      setFilename(mediaFilename);
      setIsAudio(audioMode);

      // Check for source_url parameter first for platform detection (original URL)
      const sourceUrl = params.get('source_url') ? decodeURIComponent(params.get('source_url')!) : '';
      const urlForPlatformDetection = sourceUrl || decodedDownloadUrl;

      // Create proper title format: Processing | platform | video title
      const platformName = getPlatformName(urlForPlatformDetection);
      const initialTitle = `Processing | ${platformName} | ${mediaTitle}`;
      setTitle(initialTitle);
      document.title = initialTitle;

      // Parse and set media info
      const resolution = params.get('resolution');
      const filesize = params.get('filesize');
      const bitrate = params.get('bitrate');
      const thumbnail_url = params.get('thumbnail_url');

      const info: MediaInfo = {};
      if (resolution) info.resolution = decodeURIComponent(resolution);
      if (filesize) info.filesize = parseInt(filesize);
      if (bitrate) info.bitrate = parseFloat(bitrate);
      if (thumbnail_url) info.thumbnail_url = decodeURIComponent(thumbnail_url);
      
      // Add original URL and platform info for merge operations
      if (sourceUrl) {
        info.originalUrl = sourceUrl;
        info.platform = getPlatformName(sourceUrl);
      }

      setMediaInfo(info);
      // Show media info if we have any meaningful data or original URL
      setShowMediaInfo(Boolean(resolution || filesize || bitrate || sourceUrl));

      handleMergeTask(decodedDownloadUrl, mediaTitle, platformName);
      return;
    }

    // Handle direct video/audio preview
    handleDirectPreview(params);
  };

  const handleDirectPreview = (params: URLSearchParams) => {
    const url = params.get('url') ? decodeURIComponent(params.get('url')!) : '';
    const mediaTitle = params.get('title') ? decodeURIComponent(params.get('title')!) : 'Untitled';
    const mediaFilename = params.get('filename') ? decodeURIComponent(params.get('filename')!) : 'download';
    const audioMode = params.get('is_audio') === 'true';

    // Check for source_url parameter first for platform detection (original URL)
    const sourceUrl = params.get('source_url') ? decodeURIComponent(params.get('source_url')!) : '';
    const urlForPlatformDetection = sourceUrl || url;

    // Create proper title format: Preview | platform | video title
    const platformName = getPlatformName(urlForPlatformDetection);
    const formattedTitle = `Preview | ${platformName} | ${mediaTitle}`;

    setTitle(formattedTitle);
    document.title = formattedTitle;

    setFilename(mediaFilename);
    setIsAudio(audioMode);
    setDownloadUrl(url);

    // Parse media info
    const resolution = params.get('resolution');
    const filesize = params.get('filesize');
    const bitrate = params.get('bitrate');
    const thumbnail_url = params.get('thumbnail_url');

    const info: MediaInfo = {};
    if (resolution) info.resolution = decodeURIComponent(resolution);
    if (filesize) info.filesize = parseInt(filesize);
    if (bitrate) info.bitrate = parseFloat(bitrate);
    if (thumbnail_url) info.thumbnail_url = decodeURIComponent(thumbnail_url);
    
    // Add original URL and platform info
    if (sourceUrl) {
      info.originalUrl = sourceUrl;
      info.platform = getPlatformName(sourceUrl);
    }

    setMediaInfo(info);
    setShowMediaInfo(true); // Always show if we have any info including original URL

    // Create and setup player
    setTimeout(() => {
      if (url) {
        createPlayer(url, audioMode);
      } else {
        setTitle('Error: No URL provided');
      }
    }, 100);
  };

  const getPlatformName = (url: string): string => {
    if (url.includes('youtube.com') || url.includes('youtu.be')) return 'YouTube';
    if (url.includes('tiktok.com')) return 'TikTok';
    if (url.includes('twitter.com') || url.includes('x.com')) return 'Twitter';
    if (url.includes('facebook.com')) return 'Facebook';
    if (url.includes('vimeo.com')) return 'Vimeo';
    if (url.includes('bilibili.com')) return 'Bilibili';
    if (url.includes('xiaohongshu.com')) return 'Xiaohongshu';
    if (url.includes('soundcloud.com') || url.includes('snd.sc')) return 'SoundCloud';
    if (url.includes('instagram.com')) return 'Instagram';
    if (url.includes('podcasts.apple.com') || url.includes('itunes.apple.com')) return 'Apple Podcasts';
    if (url.includes('player.fm')) return 'Player.FM';
    if (url.includes('tunein.com')) return 'TuneIn';
    if (url.includes('castbox.fm')) return 'Castbox';
    if (url.includes('spotify.com') || url.includes('spoti.fi')) return 'Spotify';
    if (url.includes('xiaoyuzhoufm.com') || url.includes('xyzcdn.net')) return 'Xiaoyuzhou';
    return 'Unknown Platform';
  };

  const handleRefreshExtract = () => {
    const params = new URLSearchParams(window.location.search);
    const sourceUrl = params.get('source_url') ? decodeURIComponent(params.get('source_url')!) : '';
    const originalUrl = sourceUrl || downloadUrl;

    if (originalUrl) {
      // Store the original URL in sessionStorage to avoid Vite dev server 403 errors
      try {
        sessionStorage.setItem('refreshExtractUrl', originalUrl);
      } catch (err) {
        console.warn('Could not store URL in sessionStorage:', err);
      }
    }
    
    // Redirect to homepage (without URL parameter to avoid Vite restrictions)
    window.location.href = '/';
  };

  const handleCheckOtherResolutions = () => {
    const params = new URLSearchParams(window.location.search);
    const sourceUrl = params.get('source_url') ? decodeURIComponent(params.get('source_url')!) : '';
    const originalUrl = sourceUrl || downloadUrl;

    if (originalUrl) {
      // Store the original URL in sessionStorage
      try {
        sessionStorage.setItem('refreshExtractUrl', originalUrl);
      } catch (err) {
        console.warn('Could not store URL in sessionStorage:', err);
      }
    }
    
    // Redirect to homepage where the URL will be auto-filled
    window.location.href = '/';
  };

  const handleMergeTask = async (mergeUrl: string, mediaTitle?: string, platformName?: string) => {
    // Guard against multiple merge tasks
    if (mergeTaskStartedRef.current || processedUrlRef.current === mergeUrl) {
      return;
    }

    mergeTaskStartedRef.current = true;
    processedUrlRef.current = mergeUrl;

    // Only update title if not already set with proper format
    if (!mediaTitle || !platformName) {
      setTitle('Processing Video...');
    }
    setShowProgress(true);

    try {
      // Start the merge task using the provided URL
      setProgress({ message: 'Starting video processing...', percentage: 0, details: '' });

      const response = await fetch(mergeUrl);
      const data = await response.json();

      if (!data.task_id) {
        throw new Error('Failed to start merge task');
      }

      const taskId = data.task_id;
      currentTaskIdRef.current = taskId;

      // Poll for progress with exponential backoff
      await pollTaskStatus(taskId);

    } catch (error) {
      mergeTaskStartedRef.current = false; // Reset on error so user can retry
      setShowProgress(false);
      setProgress({
        message: 'Processing Failed',
        percentage: 0,
        details: error instanceof Error ? error.message : 'Unknown error occurred'
      });
    }
  };

  const pollTaskStatus = async (taskId: string) => {
    return new Promise<void>((resolve, reject) => {
      let completed = false;
      let attempts = 0;
      const maxAttempts = 60;
      let pollInterval = 1000; // Start with 1 second
      const maxPollInterval = 5000; // Max 5 seconds between polls
      let consecutiveErrors = 0;

      const checkStatus = async () => {
        if (completed || attempts >= maxAttempts) {
          if (!completed) {
            reject(new Error('Video processing timed out'));
          }
          return;
        }

        attempts++;

        try {
          const response = await fetch(`/api/download/status/${taskId}`);

          if (!response.ok) {
            // Handle case where task completed and was cleaned up (404)
            if (response.status === 404) {
              completed = true;

              // Try to access the file directly
              const fileResponse = await fetch(`/api/download/file/${taskId}`, { method: 'HEAD' });

              if (fileResponse.ok) {
                // File exists, proceed to show video
                setProgress({ message: 'Processing complete! Loading video...', percentage: 100, details: '' });

                // Set default media info if not already set
                if (Object.keys(mediaInfo).length === 0) {
                  const params = new URLSearchParams(window.location.search);
                  const resolution = params.get('resolution');
                  const filesize = params.get('filesize');
                  const bitrate = params.get('bitrate');
                  const sourceUrl = params.get('source_url') ? decodeURIComponent(params.get('source_url')!) : '';

                  const info: MediaInfo = {};
                  if (resolution) info.resolution = decodeURIComponent(resolution);
                  if (filesize) info.filesize = parseInt(filesize);
                  if (bitrate) info.bitrate = parseFloat(bitrate);
                  
                  // Add original URL and platform info
                  if (sourceUrl) {
                    info.originalUrl = sourceUrl;
                    info.platform = getPlatformName(sourceUrl);
                  }

                  if (Object.keys(info).length > 0) {
                    setMediaInfo(info);
                    setShowMediaInfo(true);
                  }
                }

                const processedVideoUrl = `/api/download/file/${taskId}`;
                showVideoPreview(processedVideoUrl, taskId);
                resolve();
                return;
              } else {
                // File doesn't exist either, task likely failed or expired
                reject(new Error('Video processing completed but file is no longer available. The file may have expired or been cleaned up. Please try downloading again.'));
                return;
              }
            }

            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const statusData = await response.json();
          consecutiveErrors = 0; // Reset error counter on successful request

          console.log(`Task status (attempt ${attempts}):`, statusData);

          if (statusData.status === 'complete') {
            completed = true;
            setProgress({ message: 'Processing complete! Loading video...', percentage: 100, details: '' });

            // Update media info with actual file sizes if available
            if (statusData.actual_sizes) {
              setMediaInfo(prev => ({
                ...prev,
                actualSizes: statusData.actual_sizes,
                filesize: statusData.actual_sizes.final_file,
                streamsize: statusData.actual_sizes.combined_streams
              }));
              setShowMediaInfo(true);
            }

            // Get the processed video URL
            const processedVideoUrl = `/api/download/file/${taskId}`;

            // Switch back to preview mode with the processed video
            showVideoPreview(processedVideoUrl, taskId);
            resolve();

          } else if (statusData.status === 'error') {
            completed = true;
            const errorMessage = statusData.message || 'Video processing failed';

            // Enhanced error handling for YouTube format issues
            if (errorMessage.includes('format') && (errorMessage.includes('not available') || errorMessage.includes('unavailable'))) {
              if (errorMessage.includes('🎵') || errorMessage.includes('🎬') || errorMessage.includes('📱')) {
                reject(new Error(errorMessage));
              } else {
                reject(new Error(`${errorMessage}\n\n💡 Tip: Try refreshing the homepage and selecting a different video quality.`));
              }
            } else {
              reject(new Error(errorMessage));
            }

          } else {
            // Update progress with detailed information
            const progress = statusData.progress || 0;
            const message = statusData.message || 'Processing...';
            setProgress({
              message,
              percentage: progress,
              details: 'Please keep this page open while we process your file.'
            });

            // Implement exponential backoff for active tasks
            if (progress > 0 && progress < 50) {
              pollInterval = Math.min(pollInterval, 2000);
            } else if (progress >= 50) {
              pollInterval = Math.min(pollInterval * 1.2, maxPollInterval);
            }

            // Continue polling
            setTimeout(checkStatus, pollInterval);
          }
        } catch (pollError) {
          consecutiveErrors++;

          // Exponential backoff on errors
          pollInterval = Math.min(pollInterval * 1.5, maxPollInterval);

          // If we have too many consecutive errors, fail fast
          if (consecutiveErrors >= 5) {
            completed = true;
            reject(new Error(`Too many consecutive polling errors: ${pollError instanceof Error ? pollError.message : 'Unknown error'}`));
            return;
          }

          // Continue polling after error
          setTimeout(checkStatus, pollInterval);
        }
      };

      // Start polling
      checkStatus();
    });
  };

  const createPlayer = (url: string, audioMode: boolean) => {
    if (!playerRef.current) return;

    // Clear any existing player
    const container = playerRef.current.parentElement;
    if (!container) return;

    // Cleanup existing HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const isHlsStream = url.includes('.m3u8') || url.includes('vimeocdn.com');

    if (audioMode) {
             const audio = document.createElement('audio');
       audio.className = 'w-full';
       audio.controls = true;
       (audio as any).playsInline = true;  // TypeScript doesn't recognize this property
       audio.crossOrigin = 'anonymous';
       audio.preload = 'metadata';
      audio.src = getPlayerSrc(url);

      // Mobile-specific audio event handlers
      audio.addEventListener('error', (e) => {
        console.error('Audio loading error:', e);
        console.error('Audio error details:', {
          error: audio.error,
          networkState: audio.networkState,
          readyState: audio.readyState,
          src: audio.src
        });
        
        // Enhanced error detection
        const isTikTokProxy = url.includes('tiktok_proxy') || url.includes('session_token');
        
        setMediaError({
          hasError: true,
          errorType: isTikTokProxy ? 'proxy_expired' : 'network',
          message: isTikTokProxy 
            ? 'The audio preview session has expired. This usually happens when the temporary download link is no longer valid.'
            : 'The audio file could not be loaded. This may be due to network issues or the file is no longer available.',
          isRetryable: true
        });
      });

      audio.addEventListener('loadstart', () => {
        console.log('Audio loading started');
        // Reset error state when successfully starting to load
        setMediaError({
          hasError: false,
          errorType: 'unknown',
          message: '',
          isRetryable: false
        });
      });

      audio.addEventListener('canplay', () => console.log('Audio can play'));

      container.innerHTML = '';
      container.appendChild(audio);
      return;
    }

    // Create video element with mobile-specific attributes
    const video = document.createElement('video');
    video.className = 'w-full h-auto rounded-lg shadow-inner bg-black';
    video.controls = true;
    video.playsInline = true;
    video.crossOrigin = 'anonymous';
    video.preload = 'metadata';
    
    // Mobile-specific attributes for better iOS Safari compatibility
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('x5-playsinline', 'true');
    video.setAttribute('x5-video-player-type', 'h5');
    video.setAttribute('x5-video-player-fullscreen', 'true');

    // Enhanced error handling for mobile
    video.addEventListener('error', (e) => {
      console.error('Video loading error:', e);
      console.error('Video error details:', {
        error: video.error,
        networkState: video.networkState,
        readyState: video.readyState,
        src: video.src,
        userAgent: navigator.userAgent
      });

      // Enhanced error detection
      const isTikTokProxy = url.includes('tiktok_proxy') || url.includes('session_token');
      
      if (isTikTokProxy) {
        // Handle expired TikTok proxy sessions
        setMediaError({
          hasError: true,
          errorType: 'proxy_expired',
          message: 'The video preview session has expired. This usually happens when the temporary download link is no longer valid.',
          isRetryable: true
        });
      } else {
        // Keep existing behavior for codec/browser compatibility issues
        setMediaError({
          hasError: true,
          errorType: 'codec',
          message: 'The video was processed successfully but cannot be previewed on your device. This may be due to browser compatibility or codec support.',
          isRetryable: false
        });
        
        // Show mobile-friendly error message (keep existing behavior)
        const errorDiv = document.createElement('div');
        errorDiv.className = 'w-full h-64 bg-gray-800 rounded-lg shadow-inner flex items-center justify-center';
        errorDiv.innerHTML = `
          <div class="text-center text-white p-4">
            <div class="text-4xl mb-4">⚠️</div>
            <div class="text-lg font-semibold mb-2">Video Preview Unavailable</div>
            <div class="text-sm text-gray-300 mb-4">
              The video was processed successfully but cannot be previewed on your device.<br>
              This may be due to browser compatibility or codec support.
            </div>
            <div className="text-xs text-gray-400">
              You can still download the video using the button below.
            </div>
          </div>
        `;
        container.innerHTML = '';
        container.appendChild(errorDiv);
      }
    });

    // Additional mobile debugging events
    video.addEventListener('loadstart', () => {
      console.log('Video loading started');
      // Reset error state when successfully starting to load
      setMediaError({
        hasError: false,
        errorType: 'unknown',
        message: '',
        isRetryable: false
      });
    });
    video.addEventListener('loadedmetadata', () => console.log('Video metadata loaded'));
    video.addEventListener('canplay', () => console.log('Video can play'));
    video.addEventListener('loadeddata', () => console.log('Video data loaded'));
    video.addEventListener('stalled', () => console.warn('Video playback stalled'));
    video.addEventListener('suspend', () => console.warn('Video playback suspended'));
    video.addEventListener('waiting', () => console.warn('Video waiting for data'));

    // Handle HLS streams
    if (isHlsStream && typeof window.Hls !== 'undefined') {
      setupHlsPlayer(url);
      return;
    } else if (isHlsStream && video.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      console.log('🍎 Using native HLS support for mobile Safari');
      video.src = url;
    } else {
      // Standard video
      video.src = getPlayerSrc(url);
    }

    container.innerHTML = '';
    container.appendChild(video);
  };

  const setupHlsPlayer = (url: string) => {
    if (!window.Hls || !playerRef.current) return;

    if (window.Hls.isSupported()) {
      const hls = new window.Hls({
        debug: false,
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90
      });

      hls.loadSource(url);
      hls.attachMedia(playerRef.current);

      hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
        console.log('✅ HLS manifest parsed successfully');
        playerRef.current?.play().catch(() => {
          console.log('Auto-play blocked by browser');
        });
      });

      hls.on(window.Hls.Events.ERROR, (_event: any, data: any) => {
        console.error('❌ HLS Error:', data);
        if (data.fatal) {
          hls.destroy();
        }
      });

      hlsRef.current = hls;
    }
  };

  const showVideoPreview = (videoUrl: string, _taskId: string) => {
    // Hide progress container
    setShowProgress(false);

    // Show player and media info
    setShowExpiration(true);

    // Update title from "Processing" to "Preview" or set proper title if still "Loading..."
    let updatedTitle = title;
    if (title.includes('Processing')) {
      updatedTitle = title.replace('Processing', 'Preview');
    } else if (title === 'Loading...') {
      // If title is still "Loading...", try to get it from URL parameters
      const params = new URLSearchParams(window.location.search);
      const mediaTitle = params.get('title') ? decodeURIComponent(params.get('title')!) : 'Untitled';
      const url = params.get('url') ? decodeURIComponent(params.get('url')!) : '';
      
      // Check for source_url parameter first for platform detection (original URL)
      const sourceUrl = params.get('source_url') ? decodeURIComponent(params.get('source_url')!) : '';
      const urlForPlatformDetection = sourceUrl || url;
      
      const platformName = getPlatformName(urlForPlatformDetection);
      updatedTitle = `Preview | ${platformName} | ${mediaTitle}`;
    }

    setTitle(updatedTitle);

    // Update document title as well
    const params = new URLSearchParams(window.location.search);
    const mediaTitle = params.get('title') ? decodeURIComponent(params.get('title')!) : 'Untitled';
    const url = params.get('url') ? decodeURIComponent(params.get('url')!) : '';
    const sourceUrl = params.get('source_url') ? decodeURIComponent(params.get('source_url')!) : '';
    const urlForPlatformDetection = sourceUrl || url;
    const platformName = getPlatformName(urlForPlatformDetection);
    const seoTitle = getFormattedTitle('Preview', platformName, mediaTitle, true);
    document.title = seoTitle;

    setDownloadUrl(videoUrl);

    // Create video player with the processed video
    createPlayer(videoUrl, false);

    // Start countdown timer
    startCountdownTimer();
  };

  const startCountdownTimer = () => {
    const startTime = Date.now();

    // Fetch retention period from server configuration
    fetch('/api/config')
      .then(response => response.json())
      .then(config => {
        const expirationTime = config.file_retention_seconds || 3600; // Default to 1 hour
        console.log(`⏰ Using ${config.environment} retention period: ${expirationTime} seconds`);

        const updateTimer = () => {
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          const remaining = Math.max(0, expirationTime - elapsed);

          if (remaining === 0) {
            setTimeRemaining('Expired');
            return;
          }

          const hours = Math.floor(remaining / 3600);
          const minutes = Math.floor((remaining % 3600) / 60);
          const seconds = remaining % 60;

          let timeText;
          if (hours > 0) {
            timeText = `${hours}h ${minutes}m`;
          } else if (minutes > 0) {
            timeText = `${minutes} minute${minutes !== 1 ? 's' : ''}`;
          } else {
            timeText = `${seconds} second${seconds !== 1 ? 's' : ''}`;
          }

          setTimeRemaining(timeText);

          // Update frequency based on remaining time
          let nextUpdate = 60000; // Default 1 minute
          if (remaining <= 300) { // Last 5 minutes
            nextUpdate = 1000; // Every second
          } else if (remaining <= 900) { // Last 15 minutes
            nextUpdate = 30000; // Every 30 seconds
          }

          setTimeout(updateTimer, nextUpdate);
        };

        updateTimer();
      })
      .catch(error => {
        console.warn('Could not fetch server config, using default retention period:', error);
        // Fallback to simple 1-hour countdown
        const duration = 60 * 60 * 1000; // 1 hour

        const updateTimer = () => {
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, duration - elapsed);

          if (remaining === 0) {
            setTimeRemaining('Expired');
            return;
          }

          const hours = Math.floor(remaining / (60 * 60 * 1000));
          const minutes = Math.floor((remaining % (60 * 60 * 1000)) / (60 * 1000));

          if (hours > 0) {
            setTimeRemaining(`${hours}h ${minutes}m`);
          } else {
            setTimeRemaining(`${minutes} minutes`);
          }

          setTimeout(updateTimer, 60000); // Update every minute
        };

        updateTimer();
      });
  };

  const handleDownload = async () => {
    if (!downloadUrl) return;
    if (isDownloading) return;

    setIsDownloading(true);

    try {
      const link = document.createElement('a');
      link.href = getPlayerSrc(downloadUrl);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // If user is logged in, save to download history
      if (user) {
        try {
          const token = localStorage.getItem('accessToken');
          const response = await fetch('/api/auth/download-history', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              url: mediaInfo.originalUrl,
              title: title,
              platform: mediaInfo.platform,
              format: isAudio ? 'audio' : 'video',
              quality: mediaInfo.resolution,
              file_size: mediaInfo.filesize,
              thumbnail_url: mediaInfo.thumbnail_url,
            }),
          });
          if (response.ok) {
            console.log('✅ Download saved to history');
          } else {
            console.error('Failed to save download to history');
          }
        } catch (historyError) {
          console.error('Error saving to history:', historyError);
        }
      }

      // Track successful download
      trackEvent('download_success', {
        event_category: 'Download',
        event_label: filename,
      });

      // Re-enable the button after a short delay to prevent spamming
      // but allow the user to re-download if needed.
      setTimeout(() => {
        setIsDownloading(false);
      }, 3000);

    } catch (error) {
      console.error('Download failed:', error);
      alert('Error: Could not start the download. Please check your connection or try again.');
    }
  };

  const formatFilesize = (bytes: number) => {
    if (bytes >= 1024 * 1024) {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  const formatBitrate = (bitrate: number) => {
    if (bitrate >= 1000) {
      return `${(bitrate / 1000).toFixed(1)} Mbps`;
    }
    return `${bitrate.toFixed(0)} kbps`;
  };

  const getPlayerSrc = (url: string) => {
    const isHlsStream = url.includes('.m3u8') || url.includes('vimeocdn.com');
    
    // Detect mobile browsers
    const isMobile = /iPhone|iPad|iPod|Android|BlackBerry|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    
    console.log('🔍 Browser detection:', { isMobile, isIOS, isSafari, userAgent: navigator.userAgent });

    // For iOS Safari, we need special handling to prevent auto-downloads
    if (isIOS && isSafari) {
      console.log('🍎 iOS Safari detected - using special video serving logic');
      
      // For HLS streams on iOS Safari, use the direct URL (native HLS support)
      if (isHlsStream) {
        console.log('🎥 Using native HLS for iOS Safari');
        return url;
      }
      
      // For regular videos, ensure we're using the proxied version with mobile-friendly headers
      if (url && !url.startsWith('/api/')) {
        console.log('📱 Using proxied URL for iOS Safari compatibility');
        return `/api/proxied_download?url=${encodeURIComponent(url)}`;
      }
    }
    
    // For other mobile browsers
    if (isMobile) {
      console.log('📱 Mobile browser detected - using proxied URL for compatibility');
      
      // Use proxied URL for mobile browsers to avoid CORS issues
      if (url && !url.startsWith('/api/') && !isHlsStream) {
        return `/api/proxied_download?url=${encodeURIComponent(url)}`;
      }
    }
    
    // Desktop or already processed URLs
    if (url && !url.startsWith('/api/') && !isHlsStream) {
      return `/api/proxied_download?url=${encodeURIComponent(url)}`;
    }
    
    return url;
  };

  const cleanVideoTitle = (rawTitle: string): string => {
    try {
      let title = rawTitle;
      
      // Decode URL-encoded characters (handle potential double encoding)
      try {
        title = decodeURIComponent(title);
        // Try to decode again in case of double encoding
        if (title.includes('%')) {
          title = decodeURIComponent(title);
        }
      } catch (decodeError) {
        // If decoding fails, use the original title
        console.warn('Failed to decode title:', decodeError);
        title = rawTitle;
      }
      
      // Clean up the title
      title = title
        .replace(/^(Preview|Processing)\s*\|\s*[^|]+\s*\|\s*/, '') // Remove "Preview | Platform |" prefix
        .replace(/"/g, '') // Remove quotes
        .replace(/'/g, '') // Remove single quotes
        .replace(/[<>]/g, '') // Remove potential HTML brackets
        .trim();
      
      // Limit length for social media (Twitter has ~280 char limit, leave room for our message)
      const maxLength = 100;
      if (title.length > maxLength) {
        title = title.substring(0, maxLength).trim() + '...';
      }
      
      // Return cleaned title or fallback
      return title || 'Check out this video';
      
    } catch (error) {
      console.warn('Error cleaning video title:', error);
      return 'Check out this video';
    }
  };

  const handleShareOption = async (option: string) => {
    setShowShareMenu(false);
    
    // Reset status immediately to allow retries
    setShareStatus('idle');

    try {
      setShareStatus('loading');
      setShareMessage('Preparing share...');

      // Get or create short URL
      const params = new URLSearchParams(window.location.search);
      const previewParams = {
        url: params.get('url') || downloadUrl,
        title: params.get('title') || title.replace(/^(Preview|Processing)\s*\|\s*[^|]+\s*\|\s*/, ''),
        filename: params.get('filename') || filename,
        is_audio: isAudio,
        resolution: params.get('resolution') || mediaInfo.resolution,
        filesize: params.get('filesize') ? parseInt(params.get('filesize')!) : mediaInfo.filesize,
        bitrate: params.get('bitrate') ? parseFloat(params.get('bitrate')!) : mediaInfo.bitrate,
        source_url: params.get('source_url'),
        thumbnail_url: params.get('thumbnail_url')
      };

      const cleanParams = Object.fromEntries(
        Object.entries(previewParams).filter(([_, value]) => value !== undefined && value !== null && value !== '')
      );

      const backendUrl = getBackendUrl();
      // The body must be wrapped in a `params` object to match the backend API.
      const payload = { params: cleanParams };

      const response = await fetch(`${backendUrl}/api/shorten-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: response.statusText }));
        console.error('Error sharing:', errorData);
        throw new Error(`Failed to create short URL: ${errorData.error || response.statusText}`);
      }

      const data = await response.json();
      const shortUrl = data.short_url;
      
      // Extract clean title for sharing
      const rawTitle = String(cleanParams.title || 'Check out this video');
      // Decode URL-encoded title and clean it up for sharing
      const videoTitle = cleanVideoTitle(rawTitle);
      const sourceUrl = cleanParams.source_url ? String(cleanParams.source_url) : '';
      const platformName = sourceUrl ? getPlatformName(sourceUrl) : 'Unknown Platform';
      
      // Generate appropriate share content
      const shareContent = generateShareContent(videoTitle, platformName, isAudio);

      switch (option) {
        case 'copy':
          try {
            if (navigator.clipboard && window.isSecureContext) {
              await navigator.clipboard.writeText(shortUrl);
              setShareStatus('success');
              setShareMessage('Link copied to clipboard!');
            } else {
              // Fallback for non-secure contexts or older browsers
              const textArea = document.createElement('textarea');
              textArea.value = shortUrl;
              textArea.style.position = 'fixed'; // Avoid scrolling to bottom
              document.body.appendChild(textArea);
              textArea.focus();
              textArea.select();
              const successful = document.execCommand('copy');
              document.body.removeChild(textArea);

              if (successful) {
                setShareStatus('success');
                setShareMessage('Link copied to clipboard!');
              } else {
                throw new Error('Fallback copy failed');
              }
            }
          } catch (err) {
            console.error('Copy to clipboard failed:', err);
            setShareStatus('error');
            if (err instanceof Error && err.name === 'NotAllowedError') {
              setShareMessage('Clipboard access denied. Please allow clipboard permissions.');
            } else {
              setShareMessage('Could not copy link.');
            }
          }
          break;

        case 'twitter':
          const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareContent.twitter.text)}&url=${encodeURIComponent(shortUrl)}`;
          window.open(twitterUrl, '_blank', 'width=550,height=420');
          setShareStatus('success');
          setShareMessage('Opening Twitter...');
          break;

        case 'facebook':
          const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shortUrl)}&quote=${encodeURIComponent(shareContent.facebook.text)}`;
          window.open(facebookUrl, '_blank', 'width=550,height=420');
          setShareStatus('success');
          setShareMessage('Opening Facebook...');
          break;

        case 'whatsapp':
          const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareContent.whatsapp.text + ' ' + shortUrl)}`;
          window.open(whatsappUrl, '_blank');
          setShareStatus('success');
          setShareMessage('Opening WhatsApp...');
          break;

        case 'email':
          const emailUrl = `mailto:?subject=${encodeURIComponent(shareContent.email.subject)}&body=${encodeURIComponent(shareContent.email.body + '\n\n' + shortUrl)}`;
          window.open(emailUrl);
          setShareStatus('success');
          setShareMessage('Opening email client...');
          break;

        case 'native':
          if (navigator.share) {
            try {
              await navigator.share({
                title: shareContent.native.title,
                text: shareContent.native.text,
                url: shortUrl,
              });
              setShareStatus('success');
              setShareMessage('Shared successfully!');
            } catch (err) {
              // User cancelled or share failed
              if ((err as DOMException).name !== 'AbortError') {
                console.error('Native share failed:', err);
                setShareStatus('error');
                setShareMessage('Sharing failed.');
              } else {
                setShareStatus('idle'); // Reset if cancelled
                setShareMessage('');
              }
            }
          } else {
            // Fallback to copy for browsers that do not support navigator.share
            try {
              if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(shortUrl);
                setShareStatus('success');
                setShareMessage('Link copied! Share API not supported.');
              } else {
                const textArea = document.createElement('textarea');
                textArea.value = shortUrl;
                textArea.style.position = 'fixed';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                const successful = document.execCommand('copy');
                document.body.removeChild(textArea);

                if (successful) {
                  setShareStatus('success');
                  setShareMessage('Link copied! Share API not supported.');
                } else {
                  throw new Error('Fallback copy failed');
                }
              }
            } catch (err) {
              console.error('Copy to clipboard failed:', err);
              setShareStatus('error');
              if (err instanceof Error && err.name === 'NotAllowedError') {
                setShareMessage('Clipboard access denied. Please allow clipboard permissions.');
              } else {
                setShareMessage('Could not copy link.');
              }
            }
          }
          break;

        default:
          throw new Error('Unknown share option');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      setShareStatus('error');
      setShareMessage('Failed to share. Please try again.');
    } finally {
      // Reset status after a delay to show success/error message
      if (shareStatus !== 'loading') {
        setTimeout(() => {
          setShareStatus('idle');
          setShareMessage('');
        }, 2500);
      }
    }
  };

  const generateShareContent = (videoTitle: string, platformName: string, isAudio: boolean) => {
    const contentType = isAudio ? 'audio' : 'video';
    const emoji = isAudio ? '🎵' : '🎬';
    const action = isAudio ? 'Listen' : 'Watch';
    
    // Create a shorter, cleaner title for sharing (remove hashtags for cleaner look)
    const cleanTitle = videoTitle.length > 60 ? `${videoTitle.substring(0, 60)}...` : videoTitle;
    
    // Get current domain for branding
    const currentDomain = window.location.hostname;
    
    return {
      twitter: {
        text: `${emoji} Found this great ${contentType} via ${currentDomain}:\n\n${cleanTitle}\n\n${action} here:`
      },
      facebook: {
        text: `${emoji} ${cleanTitle} (${platformName})`
      },
      whatsapp: {
        text: `${emoji} Check this out: ${cleanTitle}\n\nFrom ${platformName} -`
      },
      email: {
        subject: `${emoji} ${contentType} from ${platformName}`,
        body: `Hi!\n\nI found this interesting ${contentType} and thought you might like it:\n\n${cleanTitle}\n\nFrom: ${platformName}\n\n${action} here:`
      },
      native: {
        title: `${emoji} ${action}: ${cleanTitle}`,
        text: `${emoji} ${action}: ${cleanTitle}`
      }
    };
  };

  const platform = mediaInfo.platform || 'Unknown';
  const videoTitle = title.split('|').pop()?.trim() || 'Untitled';

  const getMediaType = (platform: string, isAudio: boolean): string => {
    if (!isAudio) {
      return 'Video';
    }
    const podcastPlatforms = ['Apple Podcasts', 'Player.FM', 'TuneIn', 'Castbox', 'Xiaoyuzhou'];
    if (podcastPlatforms.includes(platform)) {
      return 'Podcast';
    }
    return 'Audio';
  };
  const mediaType = getMediaType(platform, isAudio);

  const seoTitle = videoTitle !== 'Loading...'
    ? `Preview "${videoTitle}" - ${platform} ${mediaType}`
    : `Preview from ${platform} - High Quality ${mediaType}`;

  const seoDescription = videoTitle !== 'Loading...'
    ? `Preview "${videoTitle}" from ${platform} in HD quality. Free, fast, and secure ${mediaType.toLowerCase()} downloader.`
    : `Preview ${mediaType.toLowerCase()} from ${platform} in HD quality. Free, fast, and secure downloader.`;

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        image={mediaInfo.thumbnail_url}
        type={'video.other'}
      />
      <div
        className="min-h-screen"
        style={{
          backgroundColor: config.branding.colors.background,
          color: config.branding.colors.text.primary
        }}
      >
        <Header />

        {/* Main Content */}
        <div
          className="content-wrapper"
          style={{ backgroundColor: config.branding.colors.surface }}
        >
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8">
            <div className="max-w-4xl mx-auto">
              <div
                className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8"
                style={{
                  backgroundColor: config.branding.colors.background,
                  borderColor: config.branding.colors.surface
                }}
              >
                {/* Title */}
                <h1 className="text-2xl sm:text-3xl font-bold mb-6 text-center leading-tight">
                  {title}
                </h1>

                {/* Info Box for Multiple Formats - Show for shared URLs */}
                {mediaInfo.originalUrl && !showProgress && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-center mb-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                        <span className="fas fa-info-circle text-blue-600 text-sm"></span>
                      </div>
                      <span className="font-semibold text-blue-900 text-sm">Multiple Formats Available</span>
                    </div>
                    <div className="text-center max-w-xl mx-auto">
                      <p className="text-xs text-blue-700 leading-relaxed">
                        This {mediaInfo.platform || 'video'} is available in multiple formats and qualities. 
                        Use the "Other Formats" button below to see all available options including different video qualities, 
                        audio-only formats, and file sizes.
                      </p>
                    </div>
                  </div>
                )}

                {/* Video/Audio Player */}
                {!showProgress && downloadUrl && (
                  <div className="mb-8 bg-black rounded-lg shadow-inner overflow-hidden">
                    {isAudio ? (
                      <audio
                        ref={playerRef as React.RefObject<HTMLAudioElement>}
                        className="w-full"
                        controls
                        autoPlay
                        playsInline
                        src={getPlayerSrc(downloadUrl)}
                        onError={(e) => {
                          console.error('Audio loading error:', e);
                        }}
                        onLoadStart={() => {
                          console.log('Audio loading started');
                        }}
                        onCanPlay={() => {
                          console.log('Audio can play');
                        }}
                      />
                    ) : (
                      <video
                        ref={playerRef as React.RefObject<HTMLVideoElement>}
                        className="w-full h-auto rounded-lg shadow-inner bg-black"
                        controls
                        autoPlay
                        playsInline
                        crossOrigin="anonymous"
                        src={getPlayerSrc(downloadUrl)}
                        // Mobile-specific attributes for better iOS Safari compatibility
                        preload="metadata"
                        webkit-playsinline="true"
                        x5-playsinline="true"
                        x5-video-player-type="h5"
                        x5-video-player-fullscreen="true"
                        onError={(e) => {
                          console.error('Video loading error:', e);
                          const videoElement = e.target as HTMLVideoElement;
                          console.error('Video error details:', {
                            error: videoElement.error,
                            networkState: videoElement.networkState,
                            readyState: videoElement.readyState,
                            src: videoElement.src
                          });
                        }}
                        onLoadStart={() => {
                          console.log('Video loading started');
                        }}
                        onLoadedMetadata={() => {
                          console.log('Video metadata loaded');
                        }}
                        onCanPlay={() => {
                          console.log('Video can play');
                        }}
                        onLoadedData={() => {
                          console.log('Video data loaded');
                        }}
                        onStalled={() => {
                          console.warn('Video playback stalled');
                        }}
                        onSuspend={() => {
                          console.warn('Video playback suspended');
                        }}
                        onWaiting={() => {
                          console.warn('Video waiting for data');
                        }}
                      />
                    )}
                  </div>
                )}

                {/* Enhanced Error Handling for Media */}
                {mediaError.hasError && (
                  <div className="mb-8">
                    <div className="w-full bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 rounded-lg shadow-inner overflow-hidden">
                      <div className="p-8 text-center">
                        <div className="mb-6">
                          {mediaError.errorType === 'proxy_expired' ? (
                            <div className="w-16 h-16 mx-auto bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                              <span className="fas fa-clock text-2xl text-yellow-600"></span>
                            </div>
                          ) : mediaError.errorType === 'network' ? (
                            <div className="w-16 h-16 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-4">
                              <span className="fas fa-wifi text-2xl text-red-600"></span>
                            </div>
                          ) : mediaError.errorType === 'codec' ? (
                            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                              <span className="fas fa-exclamation-triangle text-2xl text-gray-600"></span>
                            </div>
                          ) : (
                            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                              <span className="fas fa-exclamation-triangle text-2xl text-gray-600"></span>
                            </div>
                          )}
                        </div>
                        
                        <h3 className="text-xl font-bold text-gray-800 mb-3">
                          {mediaError.errorType === 'proxy_expired' ? 'Preview Session Expired' :
                           mediaError.errorType === 'network' ? 'Content Not Available' :
                           mediaError.errorType === 'codec' ? 'Playback Not Supported' :
                           'Preview Unavailable'}
                        </h3>
                        
                        <p className="text-gray-600 mb-4 leading-relaxed max-w-md mx-auto">
                          {mediaError.message}
                        </p>
                        
                        {/* Show original URL for easy copy-paste when proxy expired */}
                        {mediaError.errorType === 'proxy_expired' && (
                          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                            <p className="text-xs text-blue-700 mb-2 font-semibold">Original URL:</p>
                            <p className="text-xs text-blue-800 font-mono break-all">
                              {mediaInfo.originalUrl || 'URL not available'}
                            </p>
                          </div>
                        )}
                        
                        {mediaError.isRetryable && (
                          <div className="space-y-3">
                            <button
                              onClick={handleRefreshExtract}
                              className="px-6 py-3 rounded-lg font-semibold text-white shadow-lg inline-flex items-center justify-center transition-all duration-200 hover:scale-105"
                              style={{ backgroundColor: config.branding.colors.primary }}
                            >
                              <span className="fas fa-home mr-2"></span>
                              Go to Homepage
                            </button>
                            
                            <div className="text-xs text-gray-500 max-w-sm mx-auto">
                              This will take you to the homepage. The original URL will be automatically filled in the "Download Now" section, so you can immediately re-download fresh content.
                            </div>
                          </div>
                        )}
                        
                        {!mediaError.isRetryable && (
                          <div className="space-y-3">
                            <button
                              onClick={() => window.location.href = '/'}
                              className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold shadow-lg inline-flex items-center justify-center transition-all duration-200 hover:bg-gray-700"
                            >
                              <span className="fas fa-home mr-2"></span>
                              Return to Homepage
                            </button>
                            
                            <div className="text-xs text-gray-500">
                              You can still try downloading the content directly
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Progress Bar */}
                {showProgress && (
                  <div className="mb-8">
                    <p className="text-center text-lg mb-4 text-gray-600">{progress.message}</p>
                    <div
                      className="w-full rounded-full h-4"
                      style={{ backgroundColor: config.branding.colors.surface }}
                    >
                      <div
                        className="h-4 rounded-full transition-all duration-500"
                        style={{
                          width: `${progress.percentage}%`,
                          background: `linear-gradient(to right, ${config.branding.colors.primary}, ${config.branding.colors.secondary})`
                        }}
                      />
                    </div>
                    <p className="text-center text-sm mt-3 text-gray-500">{progress.details}</p>
                  </div>
                )}

                {/* Media Info Box */}
                {showMediaInfo && (
                  <div
                    className="mb-8 p-6 rounded-lg border"
                    style={{
                      backgroundColor: config.branding.colors.surface,
                      borderColor: config.branding.colors.surface
                    }}
                  >
                    <div className="flex items-center justify-center mb-6">
                      <div className="w-10 h-10 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center mr-3 shadow-sm">
                        <span className="fas fa-info-circle text-gray-600"></span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800">Media Details</h3>
                    </div>
                    
                    {/* Original Source Section */}
                    {mediaInfo.originalUrl && (
                      <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                              <span className="fas fa-link text-blue-600 text-sm"></span>
                            </div>
                            <div>
                              <span className="font-semibold text-blue-900 text-sm">Original Source</span>
                              {mediaInfo.platform && (
                                <div className="text-xs text-blue-600 mt-0.5">from {mediaInfo.platform}</div>
                              )}
                            </div>
                          </div>
                          {mediaInfo.platform && (
                            <span className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded-full font-medium shadow-sm">
                              {mediaInfo.platform}
                            </span>
                          )}
                        </div>
                        <a
                          href={mediaInfo.originalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group block p-3 bg-white border border-blue-200 rounded-lg hover:border-blue-300 hover:shadow-md transition-all duration-200"
                          title="Open original content in new tab"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-mono text-blue-800 truncate">
                                {mediaInfo.originalUrl.length > 50 
                                  ? `${mediaInfo.originalUrl.substring(0, 50)}...` 
                                  : mediaInfo.originalUrl
                                }
                              </div>
                              <div className="text-xs text-blue-600 mt-1 group-hover:text-blue-700">
                                Click to open in new tab
                              </div>
                            </div>
                            <div className="ml-3 flex-shrink-0">
                              <span className="fas fa-external-link-alt text-blue-500 group-hover:text-blue-700 transition-colors"></span>
                            </div>
                          </div>
                        </a>
                      </div>
                    )}
                    
                    {/* Technical Details */}
                    {(mediaInfo.resolution || mediaInfo.filesize || mediaInfo.streamsize || mediaInfo.bitrate) && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                              <span className="fas fa-info-circle text-gray-600 text-sm"></span>
                            </div>
                            <span className="font-semibold text-gray-800 text-sm">Technical Details</span>
                          </div>
                          {mediaInfo.originalUrl && (
                            <div className="flex items-center text-xs text-blue-600">
                              <span className="fas fa-layer-group mr-1"></span>
                              <span>One of multiple formats</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="grid gap-3">
                          {mediaInfo.resolution && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center">
                                <span className="fas fa-tv text-gray-500 text-xs w-4 mr-3"></span>
                                <span className="text-sm font-medium text-gray-700">Resolution</span>
                              </div>
                              <span className="font-mono text-sm font-semibold"
                                    style={{ color: config.branding.colors.primary }}>
                                {mediaInfo.resolution}
                              </span>
                            </div>
                          )}
                          
                          {mediaInfo.filesize && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center">
                                <span className="fas fa-file-archive text-gray-500 text-xs w-4 mr-3"></span>
                                <span className="text-sm font-medium text-gray-700">
                                  {mediaInfo.actualSizes ? 'Final Filesize' : 'Est. Filesize'}
                                </span>
                              </div>
                              <span className="font-mono text-sm font-semibold"
                                    style={{ color: config.branding.colors.primary }}>
                                {formatFilesize(mediaInfo.filesize)}
                              </span>
                            </div>
                          )}
                          
                          {mediaInfo.streamsize && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center">
                                <span className="fas fa-stream text-gray-500 text-xs w-4 mr-3"></span>
                                <span className="text-sm font-medium text-gray-700"
                                      title="Combined size of video and audio streams before merging">
                                  {mediaInfo.actualSizes ? 'Stream Size' : 'Est. Streamsize'}
                                </span>
                              </div>
                              <span className="font-mono text-sm font-semibold"
                                    style={{ color: config.branding.colors.primary }}>
                                {formatFilesize(mediaInfo.streamsize)}
                              </span>
                            </div>
                          )}
                          
                          {mediaInfo.bitrate && (
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              <div className="flex items-center">
                                <span className="fas fa-tachometer-alt text-gray-500 text-xs w-4 mr-3"></span>
                                <span className="text-sm font-medium text-gray-700">Avg. Bitrate</span>
                              </div>
                              <span className="font-mono text-sm font-semibold"
                                    style={{ color: config.branding.colors.primary }}>
                                {formatBitrate(mediaInfo.bitrate)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Expiration Warning */}
                {showExpiration && (
                  <div className="mb-6 p-6 rounded-lg text-center bg-yellow-50 border border-yellow-200">
                    <div className="flex items-center justify-center mb-3">
                      <span className="fas fa-clock mr-2 text-yellow-600" />
                      <span className="font-semibold text-yellow-800">File Available for Limited Time</span>
                    </div>
                    <p className="text-sm mb-2 text-yellow-700">
                      This merged file will be available for{' '}
                      <span className="font-bold text-yellow-800">{timeRemaining}</span>
                    </p>
                    <p className="text-xs text-yellow-600">
                      You can watch the video and download it anytime within this period.
                    </p>
                  </div>
                )}

                {/* Download Section */}
                {!showProgress && downloadUrl && (
                  <div className="text-center">
                    {timeRemaining === 'Expired' ? (
                      <p className="mb-6 text-gray-500">
                        This file has expired and is no longer available for download.
                        <br />
                        <span className="text-sm">Please return to the homepage to download a fresh copy.</span>
                      </p>
                    ) : mediaError.hasError ? (
                      <p className="mb-6 text-gray-500">
                        The preview session is not available. Use the "Go to Homepage" button above to download fresh content.
                      </p>
                    ) : (
                      <p className="mb-6 text-gray-600">
                        To save the file, click the "Download" button below.
                        <br />
                        <span className="text-sm">Alternatively, you can right-click the player and select "Save as...".</span>
                      </p>
                    )}
                    
                    {/* Action Buttons - Only show when not expired and no media error */}
                    {!mediaError.hasError && timeRemaining !== 'Expired' && (
                      <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-4">
                        <button
                          className={`w-full sm:w-auto flex-grow sm:flex-grow-0 px-6 py-4 rounded-lg text-white font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center ${
                            isDownloading ? 'bg-gray-400 cursor-not-allowed' : ''
                          }`}
                          style={{ backgroundColor: isDownloading ? '' : config.branding.colors.primary }}
                          onClick={handleDownload}
                          disabled={isDownloading}
                        >
                          <div className="flex items-center justify-center">
                            {isDownloading ? (
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                            ) : (
                              <span className="fas fa-download mr-2"></span>
                            )}
                            {isDownloading ? 'Downloading...' : isAudio ? 'Download Audio' : 'Download Video'}
                          </div>
                        </button>

                        {/* Mobile-friendly Check Other Resolutions Button */}
                        {mediaInfo.originalUrl && (
                          <button
                            onClick={handleCheckOtherResolutions}
                            className="px-6 py-4 rounded-xl font-semibold text-lg shadow-lg inline-flex items-center justify-center transition-all duration-200 min-w-[200px] h-[56px] border-2 bg-white hover:bg-gray-50 transform hover:scale-105"
                            style={{ borderColor: config.branding.colors.secondary, color: config.branding.colors.secondary }}
                          >
                            <span className="fas fa-list mr-2" />
                            <span>Other Formats</span>
                          </button>
                        )}

                        {/* Enhanced Share Button with Dropdown */}
                        <div className="relative">
                          <button
                            onClick={() => setShowShareMenu(!showShareMenu)}
                            disabled={shareStatus === 'loading'}
                            className={`px-6 py-4 rounded-xl font-semibold text-lg shadow-lg inline-flex items-center justify-center transition-all duration-200 min-w-[160px] h-[56px] border-2 ${
                              shareStatus === 'loading'
                                ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-400'
                                : shareStatus === 'success'
                                ? 'bg-green-50 text-green-700 border-green-300 hover:bg-green-100'
                                : shareStatus === 'error'
                                ? 'bg-red-50 text-red-700 border-red-300 hover:bg-red-100'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 transform hover:scale-105'
                            }`}
                            style={
                              shareStatus === 'loading'
                                ? undefined
                                : shareStatus === 'success'
                                ? undefined
                                : shareStatus === 'error'
                                ? undefined
                                : shareStatus === 'idle'
                                ? { borderColor: config.branding.colors.primary, color: config.branding.colors.primary }
                                : undefined
                            }
                          >
                            <span className={`${
                              shareStatus === 'loading' ? 'fas fa-spinner fa-spin' :
                              shareStatus === 'success' ? 'fas fa-check' :
                              shareStatus === 'error' ? 'fas fa-exclamation-triangle' :
                              'fas fa-share-alt'
                            } mr-2`} />
                            <span>
                              {shareStatus === 'loading' ? 'Creating...' :
                               shareStatus === 'success' ? 'Success!' :
                               shareStatus === 'error' ? 'Failed' :
                               'Share'}
                            </span>
                            {shareStatus === 'idle' && (
                              <span className="fas fa-chevron-down ml-2 text-sm" />
                            )}
                          </button>

                          {/* Share Menu Dropdown */}
                          {showShareMenu && shareStatus === 'idle' && (
                            <div ref={shareMenuRef} className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                              <div className="py-2">
                                <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                                  Share Options
                                </div>
                                
                                <button
                                  onClick={() => {
                                    handleShareOption('copy').catch(console.error);
                                  }}
                                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center transition-colors"
                                >
                                  <span className="fas fa-copy text-gray-600 mr-3 w-5 text-center" />
                                  <div>
                                    <div className="font-medium text-gray-800">Copy Link</div>
                                    <div className="text-xs text-gray-500">Copy short URL to clipboard</div>
                                  </div>
                                </button>

                                <button
                                  onClick={() => {
                                    handleShareOption('twitter').catch(console.error);
                                  }}
                                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center transition-colors"
                                >
                                  <span className="text-black mr-3 w-5 text-center text-lg font-bold" style={{ lineHeight: 1 }}>𝕏</span>
                                  <div>
                                    <div className="font-medium text-gray-800">Share to X (Twitter)</div>
                                    <div className="text-xs text-gray-500">Share with a custom tweet</div>
                                  </div>
                                </button>

                                <button
                                  onClick={() => {
                                    handleShareOption('facebook').catch(console.error);
                                  }}
                                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center transition-colors"
                                >
                                  <span className="fab fa-facebook text-blue-600 mr-3 w-5 text-center" />
                                  <div>
                                    <div className="font-medium text-gray-800">Share to Facebook</div>
                                    <div className="text-xs text-gray-500">Share on your Facebook timeline</div>
                                  </div>
                                </button>

                                <button
                                  onClick={() => {
                                    handleShareOption('whatsapp').catch(console.error);
                                  }}
                                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center transition-colors"
                                >
                                  <span className="fab fa-whatsapp text-green-600 mr-3 w-5 text-center" />
                                  <div>
                                    <div className="font-medium text-gray-800">Share to WhatsApp</div>
                                    <div className="text-xs text-gray-500">Send via WhatsApp message</div>
                                  </div>
                                </button>

                                <button
                                  onClick={() => {
                                    handleShareOption('email').catch(console.error);
                                  }}
                                  className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center transition-colors"
                                >
                                  <span className="fas fa-envelope text-gray-600 mr-3 w-5 text-center" />
                                  <div>
                                    <div className="font-medium text-gray-800">Share via Email</div>
                                    <div className="text-xs text-gray-500">Send via email client</div>
                                  </div>
                                </button>

                                {'share' in navigator && (
                                  <button
                                    onClick={() => {
                                      handleShareOption('native').catch(console.error);
                                    }}
                                    className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center transition-colors"
                                  >
                                    <span className="fas fa-mobile-alt text-gray-600 mr-3 w-5 text-center" />
                                    <div>
                                      <div className="font-medium text-gray-800">More Options</div>
                                      <div className="text-xs text-gray-500">Use device's native share menu</div>
                                    </div>
                                  </button>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Show message for expired files */}
                    {timeRemaining === 'Expired' && (
                      <div className="text-center mt-6">
                        <a
                          href="/"
                          className="px-6 py-3 bg-gray-600 text-white rounded-lg font-semibold shadow-lg inline-flex items-center justify-center transition-all duration-200 hover:bg-gray-700"
                        >
                          <span className="fas fa-home mr-2"></span>
                          Return to Homepage
                        </a>
                      </div>
                    )}

                    {/* Share Status Message */}
                    {shareMessage && (
                      <div className={`p-3 rounded-lg text-sm ${
                        shareStatus === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
                        shareStatus === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
                        'bg-blue-50 text-blue-800 border border-blue-200'
                      }`}>
                        {shareMessage}
                      </div>
                    )}
                  </div>
                )}

                {/* Back to Downloader Link */}
                {!mediaError.hasError && (
                  <div className="text-center mt-8">
                    <a
                      href="/"
                      className="transition-colors hover:underline"
                      style={{ color: config.branding.colors.primary }}
                    >
                      ← Back to Downloader
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Consistent Footer */}
        <Footer />
      </div>
    </>
  );
};

// Add global type declarations for HLS.js
declare global {
  interface Window {
    Hls: any;
  }
}

export default PreviewPage;
